// src/services/geminiService.ts
import { Recipe } from '../types/recipe'; // Remove .ts extension
import { recipeContextManager } from './recipeContextService';
import { handleRecipeCreationRequest, handleRecipeCopyRequest, handleRecipeModificationRequest } from './recipeService';
import { modelService } from './modelService';
import { ImageGenerationModalService } from './imageModalService';
import {
  extractKeyVisualElements,
  polishPrompt,
  GeminiResponse
} from './api/geminiApiService';
// Import AI Gateway for provider-aware generation
import { legacyGenerateContent } from './ai/aiGateway';
import { detectRecipeIntent } from './assistant/intentRouter';
import { fenceUntrusted } from './assistant/security';
import { UserProfile } from '../types/user';
import { getCostForAction, CHEFCOIN_ECONOMY } from './chefcoinService';

/**
 * Formats a Recipe object into a string for context.
 * @param recipe The recipe object.
 * @returns A string representation of the recipe.
 */
export function formatRecipeToString(recipe: Recipe | null | undefined): string {
  if (!recipe) return "";
  let content = `Recipe Context:\nTitle: ${recipe.title || "Untitled Recipe"}\n`;
  if (recipe.description) content += `Description: ${recipe.description}\n`;
  if (recipe.category) content += `Category: ${recipe.category}\n`;

  if (recipe.ingredients) {
    if (typeof recipe.ingredients === 'string') {
      content += `Ingredients:\n${recipe.ingredients}\n\n`;
    } else if (Array.isArray(recipe.ingredients)) {
      const ingredientsArray = recipe.ingredients as any[];
      const ingredientsString = ingredientsArray
        .map((ing: any) => {
          if (typeof ing === 'string') return ing;
          if (ing && typeof ing === 'object' && 'name' in ing) return (ing as { name: string }).name;
          // Attempt to handle common ingredient object structures
          if (ing && typeof ing === 'object' && 'ingredient' in ing && 'quantity' in ing) {
            return `${ing.quantity || ''} ${ing.unit || ''} ${ing.ingredient}`.trim();
          }
          return JSON.stringify(ing);
        })
        .join('\n - ');
      content += `Ingredients:\n - ${ingredientsString}\n\n`;
    } else if (typeof recipe.ingredients === 'object' && recipe.ingredients !== null) {
      content += `Ingredients:\n${JSON.stringify(recipe.ingredients)}\n\n`;
    }
  }

  if (recipe.method) content += `Method/Instructions:\n${recipe.method}\n\n`;
  if (recipe.tags && Array.isArray(recipe.tags) && recipe.tags.length > 0) {
    content += `Tags: ${recipe.tags.join(', ')}\n`;
  }
  if (recipe.notes) content += `Notes: ${recipe.notes}\n`;
  content += "---\n"; // End of context marker
  return content;
}

/**
 * Generates a recipe collection summary for context (async for database stats)
 */
async function generateRecipeCollectionContext(): Promise<string> {
  try {
    const enhancedStats = await recipeContextManager.getEnhancedRecipeStats();

    let summary = `Recipe Collection Summary:\n`;
    summary += `Total recipes in collection: ${enhancedStats.total}\n`;
    summary += `Favorite recipes: ${enhancedStats.favorites}\n`;

    if (Object.keys(enhancedStats.categories).length > 0) {
      summary += `Categories: ${Object.entries(enhancedStats.categories)
        .map(([cat, count]) => `${cat} (${count})`)
        .join(', ')}\n`;
    }

    summary += `\nNote: I can search through ALL ${enhancedStats.total} recipes in your collection to find what you're looking for.\n`;

    return summary;
  } catch {
    // Fallback to store-based stats
    recipeContextManager.getContext();
    const stats = recipeContextManager.getRecipeStats();

    return `Recipe Collection Summary:\nTotal recipes: ${stats.total}\nFavorite recipes: ${stats.favorites}\n`;
  }
}

/**
 * Uses AI to extract search terms and find relevant recipes
 */
async function findRelevantRecipes(userMessage: string): Promise<Recipe[]> {
  try {
    // Use AI to extract search terms from the message
    const extractPrompt = `Extract the main food item, dish name, or ingredient from this message. Return ONLY the search term, nothing else.

Examples:
"do you have mousse recipes?" → "mousse"
"mac and cheese recipe" → "mac and cheese"
"find me a recipe with tumeric" → "turmeric"
"chocolate cake" → "chocolate cake"
"something with chicken" → "chicken"

Message: "${fenceUntrusted(userMessage, 'user message')}"

Search term:`;

    const model = await modelService.getModelForFeature('assistantChat' as any);
    const response = await legacyGenerateContent(extractPrompt, model);

    if (response.success && response.text) {
      const searchTerm = response.text.trim().replace(/['"]/g, '');

      if (searchTerm && searchTerm.length > 0) {
        const results = await recipeContextManager.searchRecipesForAssistant(searchTerm, 8);
        return results;
      }
    }

    // Fallback: simple word extraction
    const words = userMessage.toLowerCase().split(/\s+/)
      .filter(word => word.length > 3 && !['have', 'recipes', 'looking', 'want', 'need', 'find', 'show', 'recipe', 'with'].includes(word));

    if (words.length > 0) {
      const fallbackTerm = words[0];
      return await recipeContextManager.searchRecipesForAssistant(fallbackTerm, 5);
    }

    return [];
  } catch {
    return [];
  }
}

/**
 * Generates a chat response based on user message and optional recipe context.
 * This is the primary function to be called by the UI/assistant orchestrator.
 * @param userMessage The user's message/question.
 * @param currentRecipe The current recipe object, if any.
 * @param conversationHistory Array of previous messages for context.
 * @returns A promise that resolves to a GeminiResponse object.
 */
export async function getRecipeChatResponse(
  userMessage: string,
  currentRecipe?: Recipe,
  conversationHistory?: Array<{ content: string, isUser: boolean }>,
  userProfile?: UserProfile | null // <-- THIS IS THE NEW PARAMETER
): Promise<GeminiResponse> {

  // Deterministic intent routing (no model call). A model that reads attacker-
  // controlled input must not be trusted to decide which action to dispatch —
  // that would let pasted recipes / chat messages steer routing or smuggle
  // instructions. A router miss degrades to GENERAL (a harmless answer path).
  const intent = detectRecipeIntent(userMessage);
  // Fenced copy of the user message for every prompt site — the raw string is
  // still used for programmatic purposes (search, sessionStorage, parsing).
  const fencedUserMessage = fenceUntrusted(userMessage, 'user message');

  // Handle user who wants to paste/import a recipe but hasn't pasted it yet
  if (intent === 'RECIPE_IMPORT_READY') {
    return {
      success: true,
      text: `Perfect! Here's how we can do this:

**Option 1: Paste it right here**
Just copy & paste the recipe text below — from any website, blog, or document. I'll parse it, organize it, and get it ready for your collection.

**Option 2: Open the recipe form**
If you prefer to fill in the details yourself, I can open the recipe creation form.

[data-action="open-paste-form"]Open Recipe Form[/data-action]

What would you like to do?`,
      metadata: { type: 'recipe-import-ready' }
    };
  }

  // Check if user is asking about capabilities - More precise detection
  const isCapabilityQuery =
    userMessage.toLowerCase().includes('what can you do') ||
    userMessage.toLowerCase().includes('what are your capabilities') ||
    userMessage.toLowerCase().includes('what features do you have') ||
    /^what are you\?$/i.test(userMessage.trim()) ||
    /^who are you\?$/i.test(userMessage.trim()) ||
    userMessage.toLowerCase().trim() === 'help' ||
    userMessage.toLowerCase() === 'what do you do';

  const systemPrompt = isCapabilityQuery ?
    `You are the Lab Assistant, an intelligent recipe assistant.

FORMATTING INSTRUCTIONS:
- Use markdown for bolding (**text**).
- Use markdown for bullet points (* item).
- Keep the response concise and easy to read.

Respond with a brief, friendly intro and 4 key features:

Here's what I can help with:

* **Paste recipes → instant formatting** — Copy from anywhere, I'll organize it cleanly
* **Generate food photos** — Create realistic images from your recipes
* **Natural language search** — Find recipes by saying "something with turmeric"
* **Dietary modifications** — Make recipes vegan, scale servings, convert units

What would you like to try?` :
    `You are the Lab Assistant, an intelligent recipe assistant.

CORE FOCUS: Help users with cooking, recipes, and food-related questions.

RESPONSE GUIDELINES:
- Provide concise, helpful answers about cooking and recipes.
- Use bullet points for lists when appropriate.
- Keep responses focused and practical.
- Use markdown for formatting (**bold**, *italic*, * bullets).
- Be friendly but brief.
- NEVER volunteer your capabilities list unless explicitly asked.`;

  // FILE: src/services/geminiService.ts

  // ... inside the getRecipeChatResponse function, after the 'isCapabilityQuery' check ...

  let prompt = "";

  // --- START OF REPLACEMENT LOGIC ---

  if (intent.includes('CHEFCOIN_QUERY')) {
    if (!userProfile) {
      return {
        success: true,
        text: "I can only answer questions about ChefCoins for logged-in users. Please log in to see your balance and details."
      };
    }

    const systemPromptForCoins = `You are the Lab Assistant, an intelligent and helpful AI assistant. You are an expert on the ChefLab "ChefCoin" economy. Your task is to answer the user's question accurately and concisely based on the information provided below. Do not make up any rules.`;

    // Dynamically build the knowledge context
    const chefCoinKnowledgeContext = `
**ChefCoin Economy Rules:**

*   **Your Current Balance:** Your current balance is **${userProfile.chefcoins_balance} 🪙**.
*   **Earning Coins:** Standard users receive a stipend of ${CHEFCOIN_ECONOMY.STANDARD_STIPEND} 🪙 each month. Premium users receive ${CHEFCOIN_ECONOMY.PREMIUM_STIPEND} 🪙.
*   **AI Feature Costs:**
    *   Recipe Studio Generation: ${getCostForAction('STUDIO_GENERATE')} 🪙
    *   Image Generation: ${getCostForAction('IMAGE_GENERATE')} 🪙
    *   Live Recipe Modification: ${getCostForAction('RECIPE_MODIFY')} 🪙
    *   Recipe Cleanup: ${getCostForAction('RECIPE_CLEANUP')} 🪙
    *   Import from Text: ${getCostForAction('RECIPE_CREATE_FROM_TEXT')} 🪙
    *   Answering a specific recipe question: ${getCostForAction('RECIPE_QA')} 🪙
    *   General cooking advice: ${getCostForAction('GENERAL_ADVICE')} 🪙

**Answering Strategy:**
*   If asked for balance, state it clearly.
*   If asked about costs, refer to the list.
*   If asked how to earn more, explain the monthly stipend.
*   Keep your answers friendly and to the point.
`;

    prompt = `${systemPromptForCoins}\n\n${chefCoinKnowledgeContext}\n\nUser Question: "${fencedUserMessage}"\n\nAnswer:`;

    const model = await modelService.getModelForFeature('assistantChat' as any);
    return await legacyGenerateContent(prompt, model);

  } else if (intent.includes('IMAGE_REQUEST')) {
    // Handle image requests with new unified modal
    const contextRecipe = currentRecipe || recipeContextManager.getCurrentRecipe();
    if (!contextRecipe) {
      return {
        success: true,
        text: "I need a recipe context to generate an image. Please open a recipe first, then ask me to generate an image."
      };
    }

    // Open the new unified image generation modal
    ImageGenerationModalService.openModal(contextRecipe, 'assistant');

    return {
      success: true,
      text: `🎨 **I've opened the image generator for you!**

The image generation modal is now open where you can:
✨ Generate professional food photos using AI
🎯 See your current recipe image for comparison  
✏️ Customize the prompt if needed
💫 Save the perfect image directly to your recipe

The modal will automatically create a high-quality prompt based on your recipe details.`,
      metadata: {
        type: 'image-modal-opened',
        recipeId: contextRecipe.id,
        source: 'assistant'
      }
    };

  } else if (intent.includes('RECIPE_CREATION')) {
    // ... (The rest of your existing if/else if blocks for RECIPE_CREATION, RECIPE_COPY, etc., follow here) ...
  }

  if (intent.includes('RECIPE_CREATION')) {
    // TWO-STEP FLOW: First, extract recipe title and summary for user confirmation
    // This avoids wasting ChefCoins on parsing something the user didn't mean to import
    const extractionPrompt = `You are a recipe import assistant. The user has pasted text that appears to contain a recipe.

Extract the following information from the text:
1. **Recipe Title**: A short, descriptive title for the recipe (just the title, nothing else)
2. **Brief Summary**: One sentence describing what this dish is
3. **Key Details**: Number of ingredients (approximate), number of steps (approximate)

Respond in this EXACT format:
TITLE: [recipe title]
SUMMARY: [one sentence description]
DETAILS: [approximate ingredient and step count]

Here is the text:
${userMessage.substring(0, 3000)}

Extract:`;

    const model = await modelService.getModelForFeature('assistantChat' as any);
    const extractionResult = await legacyGenerateContent(extractionPrompt, model);

    if (extractionResult.success && extractionResult.text) {
      const titleMatch = extractionResult.text.match(/TITLE:\s*(.+)/i);
      const summaryMatch = extractionResult.text.match(/SUMMARY:\s*(.+)/i);
      const detailsMatch = extractionResult.text.match(/DETAILS:\s*(.+)/i);

      const extractedTitle = titleMatch ? titleMatch[1].trim() : 'Recipe';
      const extractedSummary = summaryMatch ? summaryMatch[1].trim() : '';
      const extractedDetails = detailsMatch ? detailsMatch[1].trim() : '';

      // Store raw text in sessionStorage for the confirmation handler to use
      sessionStorage.setItem('pendingImportText', userMessage);

      return {
        success: true,
        text: `Got it — this is a recipe for **${extractedTitle}**.\n\n${extractedSummary ? extractedSummary + '\n' : ''}${extractedDetails ? extractedDetails + '\n' : ''}\nWould you like me to parse and add this to your collection? This will cost 5 ChefCoins.\n\n[data-action="confirm-recipe-import"]Yes, add "${extractedTitle}"[/data-action]`,
        metadata: {
          type: 'recipe-import-pending',
          extractedTitle: extractedTitle
        }
      };
    } else {
      // Fallback: proceed with parsing if extraction fails
      const result = await handleRecipeCreationRequest(userMessage);

      if (result.success && result.data) {
        return {
          success: true,
          text: "I've structured your recipe and opened it in the editor. Review the details and save when ready!",
          metadata: {
            type: 'recipe-creation',
            formData: result.data.formData,
            structuredMarkdown: result.data.structuredMarkdown
          }
        };
      } else {
        return {
          success: true,
          text: `I couldn't parse that recipe. ${result.error || 'Please try providing clearer recipe information.'}`
        };
      }
    }
  }

  if (intent.includes('RECIPE_COPY')) {
    // Handle recipe copy requests
    const contextRecipe = currentRecipe || recipeContextManager.getCurrentRecipe();

    if (!contextRecipe) {
      return {
        success: true,
        text: "I need a recipe context to copy. Please open a recipe first, then ask me to copy it."
      };
    }

    const result = await handleRecipeCopyRequest(contextRecipe, userMessage);

    if (result.success && result.data) {
      return {
        success: true,
        text: `✅ Recipe copied! I'll open the edit form where you can modify "${result.data.originalTitle}" before saving your copy.

Would you like me to generate an image for you?

[data-action="generate-recipe-image"]Yes, Generate Image[/data-action]`,
        metadata: {
          type: 'recipe-copy',
          formData: result.data.formData
        }
      };
    } else {
      return {
        success: true,
        text: `❌ ${result.error || 'Failed to copy the recipe. Please try again.'}`
      };
    }
  }

  if (intent.includes('RECIPE_MODIFY')) {
    // Handle recipe modification requests
    const contextRecipe = currentRecipe || recipeContextManager.getCurrentRecipe();

    if (!contextRecipe) {
      return {
        success: true,
        text: "I need a recipe context to modify. Please open a recipe in edit mode first, then ask me to make changes."
      };
    }

    // Convert Recipe to RecipeFormData format for modification
    const formData = {
      title: contextRecipe.title,
      description: contextRecipe.description || '',
      category: contextRecipe.category,
      ingredients: contextRecipe.ingredients || '',
      method: contextRecipe.method || '',
      notes: contextRecipe.notes || '',
      tags: contextRecipe.tags || [],
      isFavorite: contextRecipe.isFavorite,
      share: contextRecipe.share,
      image_url: contextRecipe.image_url || '',
      has_custom_image: contextRecipe.has_custom_image || false
      // Removed user_id: should not be modified during recipe edits
      // Removed computed fields: isOwner, creatorName, creatorCountry
      // These are display-only fields that don't belong in RecipeFormData
    };

    const result = await handleRecipeModificationRequest(formData, userMessage);

    if (result.success && result.data) {
      // Dispatch custom event to update form with modified data
      const event = new CustomEvent('recipeFormUpdated', {
        detail: {
          modifiedFormData: result.data?.formData,
          changesSummary: result.data?.changesSummary || 'Recipe updated successfully'
        }
      });
      window.dispatchEvent(event);

      return {
        success: true,
        text: `✅ Recipe modified successfully!

**Changes made:** ${result.data.changesSummary}

The recipe form has been updated with your changes. You can review and save when ready.`
      };
    } else {
      return {
        success: true,
        text: `❌ ${result.error || 'Failed to modify the recipe. Please try again with a more specific request.'}`
      };
    }
  }

  if (intent.includes('SEARCH_RECIPES')) {
    // User wants to search for recipes
    try {
      const collectionContext = await generateRecipeCollectionContext();
      const relevantRecipes = await findRelevantRecipes(userMessage);

      let contextString = collectionContext;

      if (relevantRecipes.length > 0) {
        contextString += "\n\nRelevant recipes found:\n";
        relevantRecipes.forEach(recipe => {
          contextString += `\n**${recipe.title}** (${recipe.category || 'Uncategorized'})\n`;
          contextString += `Link: [${recipe.title}](/recipe/${recipe.id})\n`;
        });

        contextString += `\nFORMATTING INSTRUCTIONS:
- Present recipes as a numbered list (1., 2., 3., etc.)
- Make each recipe name a clickable link using the exact format: [Recipe Name](/recipe/ID)
- Do NOT show recipe IDs in your response
- Only mention recipes from the list above
- NEVER fabricate recipe names or IDs

`;
      } else {
        contextString += "\n\nNo specific recipes found matching your query in the current collection.\n";
      }

      prompt = `${systemPrompt}\n\n${contextString}\n\nUser Question: "${fencedUserMessage}"\n\nAnswer:`;
    } catch {
      prompt = `${systemPrompt}\n\nUser Question: "${fencedUserMessage}"\n\nAnswer:`;
    }

  } else if (intent.includes('RECIPE_QUESTION')) {
    // User is asking about a specific recipe
    // First, check if we have a current recipe context (user is viewing a recipe page)
    if (currentRecipe) {
      const recipeContextString = formatRecipeToString(currentRecipe);
      const maxContextLength = 3500;
      const truncatedRecipeContext = recipeContextString.length > maxContextLength
        ? recipeContextString.substring(0, maxContextLength) + "... [context truncated for brevity]"
        : recipeContextString;

      prompt = `${systemPrompt}\n\n${truncatedRecipeContext}\n\nUser Question about this recipe: "${fencedUserMessage}"\n\nAnswer:`;
    } else {
      // Fallback: Extract recipe name from conversation history
      const recipeExtractionPrompt = `Look at this conversation and extract the specific recipe name the user is asking about:

Previous conversation:
${conversationHistory?.slice(-5).map(msg => `${msg.isUser ? 'User' : 'Assistant'}: ${msg.content}`).join('\n') || 'No previous conversation'}

Current question: "${fencedUserMessage}"

Extract ONLY the recipe name (e.g., "Avocado Chocolate Mousse", "Classic Mac and Cheese"). If you can't determine a specific recipe, respond with "NONE".

Recipe name:`;

      const model = await modelService.getModelForFeature('assistantChat' as any);
      const extractionResponse = await legacyGenerateContent(recipeExtractionPrompt, model);
      const recipeName = extractionResponse.success && extractionResponse.text ? extractionResponse.text.trim() : 'NONE';

      if (recipeName !== 'NONE') {
        // Search for the specific recipe
        const specificRecipes = await recipeContextManager.searchRecipesForAssistant(recipeName, 3);

        if (specificRecipes.length > 0) {
          const targetRecipe = specificRecipes[0]; // Use the first match
          const recipeContextString = formatRecipeToString(targetRecipe);
          const maxContextLength = 3500;
          const truncatedRecipeContext = recipeContextString.length > maxContextLength
            ? recipeContextString.substring(0, maxContextLength) + "... [context truncated for brevity]"
            : recipeContextString;

          prompt = `${systemPrompt}\n\n${truncatedRecipeContext}\n\nUser Question about this recipe: "${fencedUserMessage}"\n\nAnswer:`;
        } else {
          prompt = `${systemPrompt}\n\nI couldn't find the specific recipe "${recipeName}" that you're asking about. Could you clarify which recipe you mean, or search for it again?\n\nUser Question: "${fencedUserMessage}"\n\nAnswer:`;
        }
      } else {
        prompt = `${systemPrompt}\n\nI need more context about which recipe you're asking about. Could you specify the recipe name?\n\nUser Question: "${fencedUserMessage}"\n\nAnswer:`;
      }
    }

  } else if (intent.includes('STUDIO_OPEN')) {
    // Handle studio opening requests
    return {
      success: true,
      text: `🎨 **Let's create something amazing together!**

I'll open the **Recipe Creation Studio** where you can choose from different chef personas and customize your recipe exactly how you want it.

[data-action="open-recipe-studio"]Open Recipe Studio[/data-action]

Or if you prefer, I can help structure a recipe from text you already have.`,
      metadata: {
        type: 'studio-open',
        shouldOpenStudio: true
      }
    };

  } else {
    // General cooking question
    let conversationContext = "";
    if (conversationHistory && conversationHistory.length > 0) {
      conversationContext = "\n\nPrevious Conversation:\n";
      conversationHistory.slice(-3).forEach((msg) => {
        const speaker = msg.isUser ? "User" : "Assistant";
        conversationContext += `${speaker}: ${msg.content}\n`;
      });
      conversationContext += "\n";
    }

    prompt = `${systemPrompt}${conversationContext}\n\nUser Question: "${fencedUserMessage}"\n\nAnswer:`;
  }
  return await legacyGenerateContent(prompt);
}



/**
 * Generate image directly for a recipe without using chat interface
 * Used for programmatic image generation (Studio auto-generation)
 */
export async function generateImageDirectlyForRecipe(
  recipe: Recipe
): Promise<{
  success: boolean;
  imageData?: string;
  error?: string;
}> {
  try {
    // Venice is the only image provider.
    // ========== IMAGE PROMPT DIAGNOSTICS ==========

    // Generate enhanced prompt using existing flow
    const keyVisuals = await extractKeyVisualElements(recipe);
    const { generateFoodPhotographyPrompt } = await import('./veniceService');
    const assembledPrompt = generateFoodPhotographyPrompt(recipe, keyVisuals);
    const finalPrompt = await polishPrompt(assembledPrompt);

    let imageData: string;

    // Use Venice for image generation
    const { generateImageWithVenice, downloadImageAsBase64 } = await import('./veniceService');

    // Get the model from admin settings
    const { getModelForFeature } = await import('./modelService');
    const imageModel = await getModelForFeature('imageGeneration' as any);

    const veniceResult = await generateImageWithVenice(recipe, finalPrompt, imageModel, false, false);

    if (!veniceResult.success) {
      return {
        success: false,
        error: veniceResult.error || 'Image generation failed'
      };
    }

    // Convert to base64 if needed
    if (veniceResult.imageUrl) {
      const base64Data = await downloadImageAsBase64(veniceResult.imageUrl);
      if (!base64Data) {
        return { success: false, error: 'Failed to download generated image' };
      }
      imageData = base64Data;
    } else if (veniceResult.imageBase64) {
      const { compressBase64Image } = await import('./veniceService');
      imageData = await compressBase64Image(veniceResult.imageBase64);
    } else {
      return { success: false, error: 'No image data received' };
    }

    return {
      success: true,
      imageData: imageData
    };

  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}


/**
 * Generate content using the AI Gateway that routes to the correct provider.
 * This replaces the old Gemini-only generateContent function.
 */
export async function generateContent(prompt: string, model?: string): Promise<GeminiResponse> {
  return await legacyGenerateContent(prompt, model);
}

/**
 * Get AI-powered suggestions to enhance a recipe query
 * Lightweight method for real-time suggestions as user types
 */
/**
 * Session-level cache for live suggestions on the studio entry screen.
 * Each unique query hits the model at most once per session (cost guard) and
 * concurrent requests for the same query share one promise (dedup).
 * Failures are evicted so they can be retried on a later debounce.
 */
const suggestionCache = new Map<string, Promise<string[]>>();

export async function getSuggestions(query: string, count: number = 4): Promise<string[]> {
  if (!query.trim() || query.trim().length < 3) {
    return [];
  }

  const cacheKey = `${count}:${query.trim().toLowerCase()}`;
  const cached = suggestionCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const request = (async (): Promise<string[]> => {
    try {
      const prompt = `You are a culinary assistant. The user wants to make: "${query}"

Suggest ${count} brief enhancements they could add to this recipe idea. Each suggestion should be:
- 2-4 words maximum
- A specific ingredient, cooking style, or twist
- Relevant to the user's query

Return ONLY a JSON array of strings, nothing else. Example: ["with garlic", "spicy version", "one-pot style"]

Your response:`;

      const response = await legacyGenerateContent(prompt);

      if (response.success && response.text) {
        // Try to parse JSON from response
        const jsonMatch = response.text.match(/\[[\s\S]*?\]/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (Array.isArray(parsed)) {
            return parsed.filter(s => typeof s === 'string').slice(0, count);
          }
        }
      }

      return [];
    } catch {
      // Evict so a transient failure isn't cached as "no suggestions" for the session.
      suggestionCache.delete(cacheKey);
      return [];
    }
  })();

  suggestionCache.set(cacheKey, request);
  return request;
}
